/* Détail d'une session d'examen : ouverture (fige le snapshot) + collecte des */
/* copies par élève (bloc 08-A). */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle, CircleNotch, FileText, Images, PencilSimpleLine, Sparkle, Stack, Warning } from '@phosphor-icons/react';
import { Link, useParams } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import ExamCopyUploadModal from '../components/exam-copy-upload-modal';
import ExamCopyBulkImportModal from '../components/exam-copy-bulk-import-modal';
import CorrectionPanel, { type CorrImage, type CorrRow, type CorrResult } from '../components/correction-panel';
import { api } from '../lib/api';
import { runWithConcurrencyLimit } from '../lib/concurrency';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

/* « Corriger toutes les copies » (NAI-218) : même discipline que « Valider
 * toutes les copies » (PR #2131) — concurrence bornée pour éviter de saturer
 * l'API de rendu/correction sur un gros lot. */
const CORRECT_ALL_CONCURRENCY = 4;
const PUBLISH_ALL_CONCURRENCY = 4;

type BulkCorrState = 'checking' | 'variant_missing' | 'running' | 'done' | 'error';
const BULK_CORR_LABEL: Record<Exclude<BulkCorrState, 'done'>, string> = {
  checking: 'Vérification…',
  variant_missing: 'Variante manquante',
  running: 'Correction en cours…',
  error: 'Échec',
};

type VariantSource = 'undetected' | 'ai_detected' | 'teacher_confirmed';
interface Copy {
  classroom_student_id: string;
  exam_copy_id: string | null;
  first_name: string;
  last_name: string;
  status: string;
  asset_count: number;
  variant_key: string | null;
  variant_source: VariantSource;
  unread_section_count: number;
}
interface Detail {
  id: string;
  status: string;
  scheduled_at: string | null;
  exam_document_id: string;
  exam_title: string;
  classroom_id: string;
  classroom_name: string;
  copies: Copy[];
  variants: { key: string; label: string }[];
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Brouillon',
  collecting: 'Collecte',
  correcting: 'Correction',
  done: 'Terminé',
};
const COPY_LABEL: Record<string, string> = {
  pending: 'À collecter',
  collected: 'Collectée',
  corrected: 'Corrigée',
  published: 'Publiée',
  absent: 'Absent(e)',
  absent_published: 'Absent(e)',
  cancelled: 'Annulé(e)',
};
const COPY_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  pending: 'outline',
  collected: 'secondary',
  corrected: 'default',
  published: 'default',
  absent: 'outline',
  absent_published: 'default',
  cancelled: 'outline',
};

const pageClass = "min-h-dvh bg-background flex flex-col";
const wrapClass = "max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full";
const rowClass = "flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-[14px] bg-card border-[1.5px] border-border rounded-card";

export default function ExamSession() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState<Copy | null>(null);
  const [openCorr, setOpenCorr] = useState<string | null>(null);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  /* null = pas encore vérifié, 'none' = aucun job existant. Le bouton
     « Import en cours… » (NAI-218) reflète un job status='running' même
     après actualisation complète (bulkImportJob est une lecture serveur) ;
     le polling (même cadence que useBulkImportJob, ~2s) s'arrête dès que
     l'état est stable (succeeded/failed/none) pour ne jamais tourner à
     vide. */
  const [bulkImportJobStatus, setBulkImportJobStatus] = useState<'running' | 'succeeded' | 'failed' | 'none' | null>(null);
  const [correctAllBusy, setCorrectAllBusy] = useState(false);
  const [publishAllBusy, setPublishAllBusy] = useState(false);
  const [copyCorrState, setCopyCorrState] = useState<Record<string, { state: BulkCorrState; message?: string }>>({});

  /* Compteur de requêtes pour `reload()` : plusieurs appels peuvent se
     chevaucher (un par copie corrigée pendant « Corriger toutes les
     copies », concurrence bornée) et revenir dans un ordre différent de
     celui de l'envoi. On ignore toute réponse qui n'est plus la plus
     récente demandée, pour ne jamais écraser `detail` par un état plus
     ancien arrivé en retard. `mounted` évite un `setState` après démontage. */
  const reloadSeqRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const reload = useCallback((opts?: { silent?: boolean }) => {
    if (!id) return;
    const seq = ++reloadSeqRef.current;
    const silent = opts?.silent ?? false;
    if (!silent) setLoading(true);
    api.exam.get
      .query({ id })
      .then((r) => {
        if (!mountedRef.current || seq !== reloadSeqRef.current) return;
        setDetail(r as Detail | null);
      })
      .catch((e) => {
        if (!mountedRef.current || seq !== reloadSeqRef.current) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!mountedRef.current || silent) return;
        setLoading(false);
      });
  }, [id]);

  useEffect(() => reload(), [reload]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let timer: number | undefined;
    const poll = () => {
      api.exam.bulkImportJob
        .query({ sessionId: id })
        .then((job) => {
          if (cancelled) return;
          const status = job?.status ?? 'none';
          setBulkImportJobStatus(status);
          if (status === 'running') timer = window.setTimeout(poll, 2_000);
        })
        .catch(() => { if (!cancelled) setBulkImportJobStatus('none'); });
    };
    poll();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [id]);

  /* Confirmation ou correction manuelle du prof sur la variante détectée par
     l'IA (spec 13) — possible à tout moment, avant ou après une correction
     déjà lancée : ne relance jamais la correction elle-même. */
  const reassignVariant = async (examCopyId: string, variantKey: string) => {
    setError(null);
    try {
      await api.exam.reassignCopyVariant.mutate({ examCopyId, variantKey });
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /* « Corriger toutes les copies » (NAI-218) : relance au clic couvre aussi
     le retry — seules les copies en échec/en attente sont reprises, jamais
     une copie déjà corrigée ni une copie déjà en cours (voir filtre sur
     `cur` ci-dessous). */
  const correctAll = useCallback(async () => {
    if (!detail) return;
    const candidates = detail.copies.filter(
      (c) => c.exam_copy_id && c.status !== 'pending' && c.status !== 'absent' && c.status !== 'absent_published',
    );
    const toProcess = candidates.filter((c) => {
      const cur = copyCorrState[c.exam_copy_id!]?.state;
      return cur !== 'done' && cur !== 'running' && cur !== 'checking';
    });
    if (toProcess.length === 0) return;
    setCorrectAllBusy(true);
    setCopyCorrState((prev) => {
      const next = { ...prev };
      for (const c of toProcess) next[c.exam_copy_id!] = { state: 'checking' };
      return next;
    });
    await runWithConcurrencyLimit(toProcess, CORRECT_ALL_CONCURRENCY, async (c) => {
      const examCopyId = c.exam_copy_id!;
      try {
        const existing = await api.exam.copyCorrection.query({ examCopyId }) as CorrRow | null;
        if (existing) {
          setCopyCorrState((prev) => ({ ...prev, [examCopyId]: { state: 'done' } }));
          return;
        }
        if (detail.variants.length >= 2 && !c.variant_key) {
          setCopyCorrState((prev) => ({ ...prev, [examCopyId]: { state: 'variant_missing' } }));
          return;
        }
        setCopyCorrState((prev) => ({ ...prev, [examCopyId]: { state: 'running' } }));
        await api.exam.correctCopy.mutate({ examCopyId });
        setCopyCorrState((prev) => ({ ...prev, [examCopyId]: { state: 'done' } }));
        /* Rafraîchit le badge de statut principal (`detail.copies[i].status`)
           dès que CETTE copie est corrigée, sans attendre la fin du lot —
           silencieux pour ne pas réafficher le squelette de chargement
           pendant que d'autres copies tournent encore en parallèle. */
        reload({ silent: true });
      } catch (e) {
        setCopyCorrState((prev) => ({
          ...prev,
          [examCopyId]: { state: 'error', message: e instanceof Error ? e.message : String(e) },
        }));
      }
    });
    setCorrectAllBusy(false);
    reload();
  }, [detail, copyCorrState, reload]);

  /* « Publier à tous » (NAI-218) : publie les copies corrigées ET les absences
     déjà déclarées mais pas encore publiées — une copie sans correction ni
     absence est ignorée silencieusement, ce n'est pas un échec. Même
     discipline que « Corriger toutes les copies » (concurrence bornée, un
     échec individuel n'empêche pas les autres). */
  const publishAll = useCallback(async () => {
    if (!detail || !id) return;
    const toPublish = detail.copies.filter(
      (c) => (c.exam_copy_id && c.status === 'corrected') || c.status === 'absent'
    );
    if (toPublish.length === 0) return;
    setPublishAllBusy(true);
    await runWithConcurrencyLimit(toPublish, PUBLISH_ALL_CONCURRENCY, async (c) => {
      try {
        if (c.status === 'absent') {
          await api.exam.publishAbsence.mutate({ sessionId: id, classroomStudentId: c.classroom_student_id });
        } else {
          await api.exam.publishCopy.mutate({ examCopyId: c.exam_copy_id!, published: true });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    });
    setPublishAllBusy(false);
    reload();
  }, [detail, id, reload]);

  /* « Annuler maintenant » / « Déclarer comme absence » (retour Yassine) sur
     une copie « À collecter » — toutes deux ciblées par sessionId +
     classroomStudentId, pas examCopyId : un élève jamais touché n'a encore
     aucune ligne exam_copies (jointure gauche côté détail de session). */
  const removeCopy = async (classroomStudentId: string) => {
    if (!id) return;
    if (!window.confirm('Retirer cet élève de la session ? Cette action est irréversible.')) return;
    setError(null);
    try {
      await api.exam.removeCopy.mutate({ sessionId: id, classroomStudentId });
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const markAbsent = async (classroomStudentId: string) => {
    if (!id) return;
    setError(null);
    try {
      await api.exam.markCopyAbsent.mutate({ sessionId: id, classroomStudentId });
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const recollectCopy = async (classroomStudentId: string) => {
    if (!id) return;
    setError(null);
    try {
      await api.exam.recollectCopy.mutate({ sessionId: id, classroomStudentId });
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /* Publication explicite de l'absence côté compte parent (GO Ismaël 08/10)
     — aucune notification, uniquement une visibilité conditionnelle. */
  const publishAbsence = async (classroomStudentId: string) => {
    if (!id) return;
    setError(null);
    try {
      await api.exam.publishAbsence.mutate({ sessionId: id, classroomStudentId });
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const openCollecte = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await api.exam.open.mutate({ id });
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={pageClass}>
        <HeaderContent width="md" showBurger>
          <h1 className={HEADER_TITLE_CLASS}>Session d'examen</h1>
        </HeaderContent>
        <div className={wrapClass}>
          <div className="flex flex-col gap-[10px]">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-[70px] rounded-card" />)}
          </div>
        </div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={pageClass}>
        <HeaderContent width="md" showBurger>
          <h1 className={HEADER_TITLE_CLASS}>Session d'examen</h1>
        </HeaderContent>
        <div className={wrapClass}>{error && <Alert>{error}</Alert>}<p className="text-muted-foreground">Session introuvable.</p></div>
      </div>
    );
  }

  const collected = detail.copies.filter((c) => c.status !== 'pending').length;
  const eligibleCopies = detail.copies.filter((c) => c.exam_copy_id && c.status !== 'pending');
  const unpublishedCopies = eligibleCopies.filter(
    (c) => c.status !== 'published' && c.status !== 'absent_published' && c.status !== 'cancelled' && c.status !== 'absent',
  );
  const publishableCopies = eligibleCopies.filter((c) => c.status === 'corrected');
  /* Absences déclarées mais non publiées (GO Ismaël 08/10) — bloquent la
     clôture au même titre que les corrections non publiées, message dédié. */
  const unpublishedAbsences = detail.copies.filter((c) => c.status === 'absent');
  /* Copies « Corrigée » dont au moins une section est restée non lue par l'IA
     (NAI-218, point 2/3) — le statut DB reste `corrected`, seul le libellé
     affiché change, et c'est la même liste qui alimente le bandeau. */
  const incompleteCopies = detail.copies.filter((c) => c.status === 'corrected' && c.unread_section_count > 0);
  const missingCopies = detail.copies.filter((c) => c.status === 'pending');

  return (
    <div className={pageClass}>
      <HeaderContent width="md" showBurger>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <h1 className={HEADER_TITLE_CLASS}>{detail.exam_title}</h1>
            <span className={HEADER_BADGE_CLASS}>{STATUS_LABEL[detail.status] ?? detail.status}</span>
          </div>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            {detail.classroom_name} · {collected}/{detail.copies.length} copie{detail.copies.length > 1 ? 's' : ''} collectée{collected > 1 ? 's' : ''}
          </p>
        </div>
      </HeaderContent>
      <div className={wrapClass}>

        {error && <Alert>{error}</Alert>}

        {detail.status !== 'draft' && missingCopies.length > 0 && (
          <Alert className="mb-3">
            <div className="flex items-start gap-2">
              <Warning size={16} weight="fill" className="shrink-0 mt-0.5 text-[var(--naia-error)]" />
              <div className="min-w-0 flex-1">
                <p className="font-bold m-0">
                  Aucune copie trouvée pour {missingCopies.length} élève{missingCopies.length > 1 ? 's' : ''} :{' '}
                  {missingCopies.map((c) => `${c.first_name} ${c.last_name}`).join(', ')}. Vérifiez les fichiers
                  importés ou l'attribution des copies.
                </p>
              </div>
            </div>
          </Alert>
        )}

        {incompleteCopies.length > 0 && (
          <Alert className="mb-3">
            <div className="flex items-start gap-2">
              <Warning size={16} weight="fill" className="shrink-0 mt-0.5 text-[var(--naia-error)]" />
              <div className="min-w-0 flex-1">
                <p className="font-bold m-0">
                  {incompleteCopies.length} copie{incompleteCopies.length > 1 ? 's' : ''} nécessite
                  {incompleteCopies.length > 1 ? 'nt' : ''} une correction manuelle
                </p>
                <ul className="list-none p-0 m-0 mt-1.5 flex flex-col gap-1.5">
                  {incompleteCopies.map((c) => (
                    <li key={c.classroom_student_id} className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-[13.5px]">
                        {c.first_name} {c.last_name} — {c.unread_section_count} exercice{c.unread_section_count > 1 ? 's' : ''} concerné{c.unread_section_count > 1 ? 's' : ''}
                      </span>
                      <Button
                        variant="secondary"
                        onClick={() => setOpenCorr((v) => (v === c.exam_copy_id ? null : c.exam_copy_id))}
                      >
                        {openCorr === c.exam_copy_id ? 'Fermer' : 'Corriger'}
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Alert>
        )}

        {detail.exam_document_id && (
          <div className="mb-3">
            <Link
              to={`/teacher/library/${detail.exam_document_id}`}
              className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-naia-blue no-underline hover:underline"
            >
              <PencilSimpleLine size={15} weight="bold" />
              Modifier le sujet
            </Link>
          </div>
        )}

        {detail.status === 'draft' && (
          <div className="text-center py-12 px-4 text-muted-foreground">
            <FileText size={40} weight="light" className="mx-auto" />
            <p className="font-extrabold text-foreground mt-2 mb-1">Session en brouillon</p>
            <p className="text-muted-foreground">Ouvrez la collecte pour figer le barème et téléverser les copies.</p>
            <Button className="mt-3" disabled={busy} onClick={() => void openCollecte()}>
              {busy ? 'Ouverture…' : 'Ouvrir la collecte'}
            </Button>
          </div>
        )}

        {detail.status === 'collecting' && (
          <div className="flex justify-end mb-3">
            <Button variant="secondary" onClick={() => setBulkImportOpen(true)}>
              {bulkImportJobStatus === 'running' ? (
                <>
                  <CircleNotch size={15} className="mr-1.5 animate-spin" />
                  Import en cours…
                </>
              ) : bulkImportJobStatus === 'succeeded' ? (
                <>
                  <Images size={15} weight="fill" className="mr-1.5" />
                  Voir les copies importées
                </>
              ) : (
                <>
                  <Stack size={15} weight="fill" className="mr-1.5" />
                  Importer des copies (groupé)
                </>
              )}
            </Button>
          </div>
        )}

        {detail.status !== 'draft' && detail.copies.some((c) => c.exam_copy_id && c.status === 'collected') && (
          <div className="flex justify-end mb-3">
            <Button variant="secondary" disabled={correctAllBusy} onClick={() => void correctAll()}>
              <Sparkle size={15} weight="fill" className="mr-1.5" />
              {correctAllBusy ? 'Correction en cours…' : 'Corriger toutes les copies'}
            </Button>
          </div>
        )}

        {detail.status !== 'draft' && (
          <div className="flex flex-col gap-[10px]">
            {detail.copies.map((c) => (
              <div key={c.classroom_student_id} className={rowClass}>
                <div className="basis-full min-w-0 sm:basis-0 sm:flex-1">
                  <div className="font-bold text-foreground truncate">{c.first_name} {c.last_name}</div>
                  <div className="text-[13px] text-muted-foreground mt-0.5">
                    {c.asset_count > 0 ? `${c.asset_count} photo${c.asset_count > 1 ? 's' : ''}` : 'Aucune photo'}
                  </div>
                  {c.exam_copy_id && copyCorrState[c.exam_copy_id]?.state === 'error' && (
                    <div className="text-[13px] text-[var(--naia-error)] mt-0.5">
                      {copyCorrState[c.exam_copy_id]!.message}
                    </div>
                  )}
                </div>
                <Badge variant={COPY_VARIANT[c.status] ?? 'outline'}>
                  {c.status === 'corrected' && c.unread_section_count > 0
                    ? `Correction incomplète (${c.unread_section_count})`
                    : COPY_LABEL[c.status] ?? c.status}
                </Badge>
                {c.status === 'absent_published' && <Badge variant="default">Publiée</Badge>}
                {c.exam_copy_id && copyCorrState[c.exam_copy_id] && copyCorrState[c.exam_copy_id]!.state !== 'done' && (() => {
                  const entry = copyCorrState[c.exam_copy_id!]!;
                  const corrState = entry.state as Exclude<BulkCorrState, 'done'>;
                  return (
                    <Badge
                      variant={corrState === 'error' ? 'outline' : 'secondary'}
                      title={corrState === 'error' ? entry.message : undefined}
                    >
                      {BULK_CORR_LABEL[corrState]}
                    </Badge>
                  );
                })()}
                {detail.variants.length >= 2 && c.exam_copy_id && c.status !== 'absent' && c.status !== 'absent_published' && c.status !== 'cancelled' && (
                  <div className="flex items-center gap-1">
                    <Select
                      items={detail.variants.map((v) => ({ value: v.key, label: v.label }))}
                      value={c.variant_key ?? null}
                      onValueChange={(v) => v && void reassignVariant(c.exam_copy_id!, v)}
                    >
                      <SelectTrigger className="!w-auto !h-9 text-[12.5px]">
                        <SelectValue placeholder="— variante —" />
                      </SelectTrigger>
                      <SelectContent>
                        {detail.variants.map((v) => (
                          <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {c.variant_key ? (
                      c.variant_source === 'teacher_confirmed' ? (
                        <CheckCircle size={15} weight="fill" className="text-naia-blue shrink-0" aria-label="Variante confirmée par vous" />
                      ) : (
                        <Sparkle size={15} weight="fill" className="text-[var(--naia-text-secondary)] shrink-0" aria-label="Variante détectée automatiquement, non confirmée" />
                      )
                    ) : (
                      <span className="flex items-center gap-1 text-[12.5px] text-[var(--naia-error)]">
                        <Warning size={15} weight="fill" className="shrink-0" aria-label="Variante non déterminée" />
                        Variante non déterminée — sélectionnez-la manuellement
                      </span>
                    )}
                  </div>
                )}
                {(detail.status === 'collecting' || c.status === 'pending') && (
                  <Button variant="secondary" onClick={() => setUpload(c)}>
                    <Camera size={15} weight="fill" className="mr-1.5" />
                    {c.asset_count > 0 ? 'Modifier' : 'Ajouter'}
                  </Button>
                )}
                {c.exam_copy_id && c.status !== 'pending' && c.status !== 'absent' && c.status !== 'absent_published' && c.status !== 'cancelled' && (
                  <Button variant="secondary" onClick={() => setOpenCorr((v) => (v === c.exam_copy_id ? null : c.exam_copy_id))}>
                    {openCorr === c.exam_copy_id ? 'Fermer' : 'Correction'}
                  </Button>
                )}
                {c.status === 'pending' && (
                  <>
                    <Button variant="secondary" onClick={() => void markAbsent(c.classroom_student_id)}>
                      Déclarer comme absence
                    </Button>
                    <Button variant="secondary" onClick={() => void removeCopy(c.classroom_student_id)}>
                      Annuler maintenant
                    </Button>
                  </>
                )}
                {c.status === 'absent' && (
                  <Button variant="secondary" onClick={() => void publishAbsence(c.classroom_student_id)}>
                    Publier
                  </Button>
                )}
                {(c.status === 'absent' || c.status === 'absent_published' || c.status === 'cancelled') && (
                  <Button variant="secondary" onClick={() => void recollectCopy(c.classroom_student_id)}>
                    Recollecter
                  </Button>
                )}
                {openCorr === c.exam_copy_id && c.exam_copy_id && (
                  <div className="basis-full mt-2.5 pt-2.5 border-t border-border">
                    <CorrectionPanel
                      key={c.exam_copy_id}
                      load={() => api.exam.copyCorrection.query({ examCopyId: c.exam_copy_id! }) as Promise<CorrRow | null>}
                      run={() => api.exam.correctCopy.mutate({ examCopyId: c.exam_copy_id! }) as Promise<CorrRow>}
                      saveOverride={(override: CorrResult) =>
                        api.exam.saveCopyOverride.mutate({ examCopyId: c.exam_copy_id!, override })
                      }
                      publish={(published: boolean) => api.exam.publishCopy.mutate({ examCopyId: c.exam_copy_id!, published })}
                      loadImages={() => api.exam.copyAssets.query({ examCopyId: c.exam_copy_id! }) as Promise<CorrImage[]>}
                      onChange={reload}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {detail.status === 'correcting' && (
          <div className="flex flex-col items-end gap-1.5 mt-4">
            <div className="flex gap-[10px] justify-end flex-wrap">
              {(publishableCopies.length > 0 || unpublishedAbsences.length > 0) && (
                <Button variant="secondary" disabled={publishAllBusy} onClick={() => void publishAll()}>
                  {publishAllBusy ? 'Publication en cours…' : 'Publier à tous'}
                </Button>
              )}
              <Button
                variant="secondary"
                disabled={busy || unpublishedCopies.length > 0 || unpublishedAbsences.length > 0 || missingCopies.length > 0}
                onClick={async () => {
                  if (!id) return;
                  setBusy(true);
                  try {
                    await api.exam.close.mutate({ id });
                    reload();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Clôturer la session
              </Button>
            </div>
            {unpublishedCopies.length > 0 && (
              <p className="text-[13px] text-muted-foreground m-0">
                {unpublishedCopies.length} correction{unpublishedCopies.length > 1 ? 's' : ''} reste{unpublishedCopies.length > 1 ? 'nt' : ''} à publier
              </p>
            )}
            {unpublishedAbsences.length > 0 && (
              <p className="text-[13px] text-muted-foreground m-0">
                {unpublishedAbsences.length} absence{unpublishedAbsences.length > 1 ? 's' : ''} reste{unpublishedAbsences.length > 1 ? 'nt' : ''} à publier
              </p>
            )}
            {missingCopies.length > 0 && (
              <p className="text-[13px] text-muted-foreground m-0">
                {missingCopies.length} élève{missingCopies.length > 1 ? 's' : ''} encore sans copie ni décision
              </p>
            )}
          </div>
        )}
      </div>

      {upload && id && (
        <ExamCopyUploadModal
          sessionId={id}
          classroomStudentId={upload.classroom_student_id}
          studentName={`${upload.first_name} ${upload.last_name}`}
          onClose={() => setUpload(null)}
          onDone={() => {
            setUpload(null);
            reload();
          }}
        />
      )}

      {bulkImportOpen && id && (
        <ExamCopyBulkImportModal
          sessionId={id}
          classroomId={detail.classroom_id}
          onClose={() => setBulkImportOpen(false)}
          onValidated={reload}
          openToRecap={bulkImportJobStatus === 'succeeded'}
          openToProcessing={bulkImportJobStatus === 'running'}
        />
      )}
    </div>
  );
}
